/**
 * Kiosk patient forms: dedicated route, cross-tab lock (localStorage), staff exit.
 * Kiosk UI only runs for a logged-in staff session (token + userProfile in local storage).
 */

import { useCallback, useEffect, useRef } from "react";
import AuthService from "../services/AuthService";
import UserService from "../services/UserService";

const KIOSK_IDLE_ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "click",
  "touchstart",
  "scroll",
];

export const KIOSK_PATIENT_FORM_PATH = "/kiosk/patient-form";
export const KIOSK_DEFAULT_RETURN_PATH = "/visits";

/** Default 15 minutes — independent of REACT_APP_SESSION_TIMEOUT (staff login). */
export const KIOSK_IDLE_TIMEOUT_MS_DEFAULT = 15 * 60 * 1000;

export function getKioskIdleTimeoutMs() {
  const rawMs = process.env.REACT_APP_KIOSK_IDLE_TIMEOUT_MS;
  if (rawMs != null && rawMs !== "") {
    const ms = Number(rawMs);
    if (Number.isFinite(ms) && ms > 0) return ms;
  }
  const rawMins = process.env.REACT_APP_KIOSK_IDLE_TIMEOUT_MINUTES;
  if (rawMins != null && rawMins !== "") {
    const mins = Number(rawMins);
    if (Number.isFinite(mins) && mins > 0) return mins * 60 * 1000;
  }
  return KIOSK_IDLE_TIMEOUT_MS_DEFAULT;
}

const KIOSK_ACTIVE = "snap_kiosk_active";
const KIOSK_PATIENT_FORM_ID = "snap_kiosk_patient_form_id";
const KIOSK_RETURN_PATH = "snap_kiosk_return_path";
const KIOSK_UNIFIED_TOKEN = "snap_kiosk_unified_token";
const KIOSK_OWNER_USER_ID = "snap_kiosk_owner_user_id";

/** Written before lock clear so other tabs can navigate to the same return path on exit. */
export const KIOSK_EXIT_SIGNAL = "snap_kiosk_exit_signal";

/** Query param set when staff launches kiosk — must match logged-in user to enter kiosk. */
export const KIOSK_LAUNCHER_QUERY_PARAM = "kiosk_launcher";

/** Keys shared across tabs via localStorage (storage event). */
export const KIOSK_LOCK_STORAGE_KEYS = [
  KIOSK_ACTIVE,
  KIOSK_PATIENT_FORM_ID,
  KIOSK_RETURN_PATH,
  KIOSK_UNIFIED_TOKEN,
  KIOSK_OWNER_USER_ID,
  KIOSK_EXIT_SIGNAL,
];

function getLocalStore() {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

function readLockItem(key) {
  const store = getLocalStore();
  if (!store) return null;
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}

function writeLockItem(key, value) {
  const store = getLocalStore();
  if (!store) return;
  try {
    store.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function removeLockItem(key) {
  const store = getLocalStore();
  if (!store) return;
  try {
    store.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** Listen for kiosk lock changes from other tabs (storage event does not fire in the same tab). */
export function subscribeKioskLockChanges(listener) {
  if (typeof window === "undefined") return () => {};
  const handler = (event) => {
    if (!event.key || !KIOSK_LOCK_STORAGE_KEYS.includes(event.key)) return;
    listener(event);
  };
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
}

/** True when URL is the dedicated kiosk patient-form route. */
export function isKioskRoute(location) {
  if (!location) return false;
  return isDedicatedKioskPath(location.pathname);
}

export function isDedicatedKioskPath(pathname) {
  const p = pathname || "";
  return p === KIOSK_PATIENT_FORM_PATH || p.startsWith(`${KIOSK_PATIENT_FORM_PATH}/`);
}

/**
 * Staff session required: access token, stored user profile, role kiosk flag, 2FA complete.
 */
export function isStaffKioskEligible() {
  if (!AuthService.isAuthenticated()) return false;
  if (AuthService.is2FAVerified() === false) return false;
  const profile = UserService.getProfile();
  if (!profile || typeof profile !== "object") return false;
  return Boolean(profile.kiosk_mode_enabled);
}

export function getCurrentKioskStaffUserId() {
  const id = AuthService.getProfileId();
  return id != null && String(id).trim() !== "" ? String(id) : null;
}

export function getKioskLauncherFromSearch(search) {
  try {
    const value = new URLSearchParams(search || "").get(KIOSK_LAUNCHER_QUERY_PARAM);
    return value != null && String(value).trim() !== "" ? String(value) : null;
  } catch {
    return null;
  }
}

/** Kiosk URL is only valid for the staff user encoded in kiosk_launcher. */
export function isKioskLauncherAuthorized(search) {
  if (!isStaffKioskEligible()) return false;
  const launcherId = getKioskLauncherFromSearch(search);
  const currentId = getCurrentKioskStaffUserId();
  if (!launcherId || !currentId) return false;
  return launcherId === currentId;
}

function isKioskSessionOwnedByCurrentUser() {
  const ownerId = readLockItem(KIOSK_OWNER_USER_ID);
  const currentId = getCurrentKioskStaffUserId();
  if (!ownerId || !currentId) return false;
  return ownerId === currentId;
}

/** Kiosk shell, route lock, and locked completion — only when URL and staff session both qualify. */
export function isKioskModeActive(location) {
  return (
    isKioskRoute(location) &&
    isStaffKioskEligible() &&
    isKioskLauncherAuthorized(location?.search)
  );
}

/** Paths patients/staff may visit while kiosk session is locked. */
export function isKioskAllowedPath(pathname) {
  return isDedicatedKioskPath(pathname);
}

/**
 * @param {string} patientFormId
 * @param {{ unifiedToken?: string|null, reentry?: boolean, manifestResend?: boolean }} [options]
 */
export function buildKioskPatientFormUrl(patientFormId, options = {}) {
  const params = new URLSearchParams();
  params.set("patient_form_id", String(patientFormId));
  const token = options.unifiedToken || getKioskUnifiedToken();
  if (token) {
    params.set("unified_token", token);
  }
  // Kiosk opens the live unified step from session (no completion / continue-anyway gate).
  if (options.manifestResend) {
    params.set("manifest_resend", "1");
  }
  const launcherId = options.kioskLauncherId ?? getCurrentKioskStaffUserId();
  if (launcherId) {
    params.set(KIOSK_LAUNCHER_QUERY_PARAM, launcherId);
  }
  return `${KIOSK_PATIENT_FORM_PATH}?${params.toString()}`;
}

export function getKioskUnifiedToken() {
  return readLockItem(KIOSK_UNIFIED_TOKEN);
}

export function getKioskResumeUrl() {
  const id = readLockItem(KIOSK_PATIENT_FORM_ID);
  if (id) {
    return buildKioskPatientFormUrl(id, { unifiedToken: getKioskUnifiedToken() });
  }
  return null;
}

export function getKioskReturnPath() {
  return readLockItem(KIOSK_RETURN_PATH) || KIOSK_DEFAULT_RETURN_PATH;
}

/** True when kiosk lock is on in this browser profile (all tabs) for the current staff user. */
export function isKioskSessionActive() {
  return (
    readLockItem(KIOSK_ACTIVE) === "1" &&
    isStaffKioskEligible() &&
    isKioskSessionOwnedByCurrentUser()
  );
}

export function activateKioskSession({
  patientFormId,
  returnPath = KIOSK_DEFAULT_RETURN_PATH,
  unifiedToken = null,
  ownerUserId = null,
}) {
  if (!isStaffKioskEligible()) return;
  const owner = ownerUserId ?? getCurrentKioskStaffUserId();
  if (!owner) return;
  removeLockItem(KIOSK_EXIT_SIGNAL);
  writeLockItem(KIOSK_ACTIVE, "1");
  writeLockItem(KIOSK_OWNER_USER_ID, owner);
  if (patientFormId) {
    writeLockItem(KIOSK_PATIENT_FORM_ID, patientFormId);
  }
  writeLockItem(KIOSK_RETURN_PATH, returnPath || KIOSK_DEFAULT_RETURN_PATH);
  if (unifiedToken) {
    writeLockItem(KIOSK_UNIFIED_TOKEN, unifiedToken);
  } else {
    removeLockItem(KIOSK_UNIFIED_TOKEN);
  }
}

/**
 * @param {{ broadcastExitToOtherTabs?: boolean, returnPath?: string }} [options]
 * When broadcastExitToOtherTabs is true, other tabs on the kiosk URL navigate to returnPath.
 */
export function clearKioskSession(options = {}) {
  if (options.broadcastExitToOtherTabs) {
    const path = options.returnPath || KIOSK_DEFAULT_RETURN_PATH;
    writeLockItem(KIOSK_EXIT_SIGNAL, path);
  }
  KIOSK_LOCK_STORAGE_KEYS.forEach((key) => {
    if (key === KIOSK_EXIT_SIGNAL && !options.broadcastExitToOtherTabs) {
      removeLockItem(key);
      return;
    }
    if (key === KIOSK_EXIT_SIGNAL) {
      return;
    }
    removeLockItem(key);
  });
}

export function consumeKioskExitSignal() {
  const path = readLockItem(KIOSK_EXIT_SIGNAL);
  if (path) {
    removeLockItem(KIOSK_EXIT_SIGNAL);
    return path;
  }
  return null;
}

/**
 * Kiosk-only inactivity timer (15 min default). Does not affect SessionTimeout / staff login.
 */
export function useKioskIdleTimer({ enabled, onIdle }) {
  const timeoutRef = useRef(null);
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  const clearIdleTimer = useCallback(() => {
    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const resetTimer = useCallback(() => {
    clearIdleTimer();
    if (!enabled) return;
    timeoutRef.current = setTimeout(() => {
      onIdleRef.current?.();
    }, getKioskIdleTimeoutMs());
  }, [enabled, clearIdleTimer]);

  useEffect(() => {
    if (!enabled) {
      clearIdleTimer();
      return undefined;
    }

    const onActivity = () => resetTimer();
    KIOSK_IDLE_ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, onActivity, { passive: true });
    });
    resetTimer();

    return () => {
      clearIdleTimer();
      KIOSK_IDLE_ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, onActivity);
      });
    };
  }, [enabled, resetTimer, clearIdleTimer]);

  return { resetTimer, clearIdleTimer };
}
